// apps/cli/src/lib/bootstrap-status.ts
import { createHash, randomUUID } from "node:crypto";
import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";

/** The durable status doc the installer writes (F3 RETRO §2). */
export interface StatusDoc {
  schemaVersion: number;
  status: "running" | "done" | "failed";
  phase: string;
  phaseIndex: number;
  phaseTotal: number;
  startedAt?: string;
  updatedAt?: string;
  detail?: string;
  result?: { gatewayFqdn?: string; gatewayUrl?: string; foundryEndpoint?: string; freshVsReused?: Record<string, boolean> };
  error?: { phase: string; message: string };
  history?: { phase: string; at: string; status: string }[];
}

export const STATUS_CONTAINER = "status";
export const STATUS_BLOB = "status.json";

/** Deterministic SA name — MUST match installer/entrypoint.sh's `m8tinst$(sha256(RG+SUB)|cut -c1-12)`. */
export function deriveStatusSaName(resourceGroup: string, subscriptionId: string): string {
  const hex = createHash("sha256").update(resourceGroup + subscriptionId).digest("hex").slice(0, 12);
  return `m8tinst${hex}`;
}

export function statusBlobUrl(saName: string): string {
  return `https://${saName}.blob.core.windows.net/${STATUS_CONTAINER}/${STATUS_BLOB}`;
}

/** Read + parse the status blob using the Owner's az creds (SA key, mgmt plane). */
export async function readStatusBlob(opts: {
  saName: string; resourceGroup: string; subscriptionId: string;
}): Promise<StatusDoc> {
  let key: string;
  try {
    key = (await runAz([
      "storage", "account", "keys", "list",
      "-n", opts.saName, "-g", opts.resourceGroup, "--subscription", opts.subscriptionId,
      "--query", "[0].value", "-o", "tsv",
    ])).trim();
    if (!key) throw new Error("empty storage-account key");
  } catch (e) {
    throw new LocalCliError({
      code: "BOOTSTRAP_STATUS_UNREADABLE",
      message: `Could not read the installer status storage account '${opts.saName}'.`,
      hint: "The installer may not have reached its first phase yet — wait a few seconds and retry.",
      cause: e,
    });
  }
  // Download to a real temp file, not `/dev/stdout`: `az storage blob download`
  // requires a SEEKABLE target stream and errors ("Target stream handle must be
  // seekable") when handed a pipe. Read the file back, then clean it up.
  const tmpFile = path.join(os.tmpdir(), `m8t-status-${randomUUID()}.json`);
  try {
    await runAz([
      "storage", "blob", "download",
      "--account-name", opts.saName, "--account-key", key,
      "--container-name", STATUS_CONTAINER, "--name", STATUS_BLOB,
      "--file", tmpFile, "--no-progress", "--only-show-errors",
    ]);
    const raw = await fs.readFile(tmpFile, "utf8");
    return JSON.parse(raw.trim()) as StatusDoc;
  } catch (e) {
    throw new LocalCliError({
      code: "BOOTSTRAP_STATUS_UNREADABLE",
      message: "The installer status blob is not readable yet.",
      hint: "Phase 1 (status storage) may not have completed. Retry shortly, or check 'az container logs'.",
      cause: e,
    });
  } finally {
    await fs.rm(tmpFile, { force: true });
  }
}
